// Legal companies and country responsibles: the PURE rules of Sentinel's
// app/services/field_ops/legal_entities.py and country_responsibles.py (FO-2, SEN-293), applied to NomOS records.
// No I/O here; buildingsAdmin.ts reads the records, asks these functions what changes, and writes it.

export const MAX_ITEMS = 5000;            // legal_entities.MAX_ITEMS
export const RESPONSE_SAMPLE_CAP = 200;   // schemas/compliance.py RESPONSE_SAMPLE_CAP

/** A rule refusal with the HTTP status and Sentinel error code the router would answer. */
export class ComplianceError extends Error {
  constructor(public status: number, public code: string, message: string) { super(message); this.name = 'ComplianceError'; }
}

export function normalizeCountry(code: string | null | undefined): string {
  const clean = (code ?? '').trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(clean)) throw new ComplianceError(422, 'invalid_country', 'country_code must be two letters, e.g. DK');
  return clean;
}

/** legal_entities.default_name: "<customer> <CC>", at most 200 characters. */
export function defaultName(customerName: string | null | undefined, countryCode: string): string {
  return `${(customerName ?? '').trim() || 'Customer'} ${countryCode}`.slice(0, 200);
}

/** One site as the rules see it (ids are the caller's own: NomOS record ids). */
export interface SiteFacts {
  id: string;
  country: string | null;      // ISO2 or null
  isActive: boolean;
  code: string | null;         // external_system_ids.jysk_code (the store code)
  legalEntity: string | null;  // legal_entity record id
  confirmed: boolean;          // legal_entity_confirmed_at is set
}

export interface EntityFacts { id: string; country: string; name: string }

// ---- propose ------------------------------------------------------------------------------------------------------

export interface ProposedCountryPlan {
  country_code: string;
  entity: EntityFacts | null;  // null: skipped, or a company still to be created (create = true)
  create: boolean;
  name: string | null;
  assign: string[];            // site ids that get the company, ASSUMED
  skipped_reason: string | null;
}

/** legal_entities.propose_defaults: one company per country with active sites and no company, and every
 *  unassigned active site of the country assigned to it (assumed). A country with several companies is skipped. */
export function planPropose(sites: SiteFacts[], entities: EntityFacts[], customerName: string | null): ProposedCountryPlan[] {
  const countries = [...new Set(sites.filter((s) => s.isActive && s.country).map((s) => s.country as string))].sort();
  const byCountry = new Map<string, EntityFacts[]>();
  for (const e of entities) byCountry.set(e.country, [...(byCountry.get(e.country) ?? []), e]);
  return countries.map((cc) => {
    const companies = byCountry.get(cc) ?? [];
    if (companies.length > 1) {
      return { country_code: cc, entity: null, create: false, name: null, assign: [],
        skipped_reason: 'several legal companies in this country: assign its buildings by hand' };
    }
    const entity = companies[0] ?? null;
    const assign = sites.filter((s) => s.isActive && s.country === cc && !s.legalEntity).map((s) => s.id).sort();
    return { country_code: cc, entity, create: !entity, name: entity ? entity.name : defaultName(customerName, cc), assign,
      skipped_reason: null };
  });
}

// ---- assign -------------------------------------------------------------------------------------------------------

export interface AssignPlan {
  assigned: string[];
  moved: Array<[string, string, string]>;  // (site, from entity, to entity)
  unchanged: string[];
  kept_confirmed: string[];
  wrong_country: string[];
  unknown_buildings: string[];
  unmatched_codes: string[];
  ambiguous_codes: string[];
}

/** legal_entities.assign: classify the named sites (by id, or by store code among ACTIVE sites) for one company.
 *  A CONFIRMED assignment to a different company is never moved unless moveConfirmed (with confirmed=true). */
export function planAssign(entity: EntityFacts, sites: SiteFacts[], opts: {
  buildingIds?: string[]; storeCodes?: string[]; confirmed?: boolean; moveConfirmed?: boolean;
}): AssignPlan {
  const ids = [...new Set(opts.buildingIds ?? [])];
  const codes = [...new Set((opts.storeCodes ?? []).map((c) => (c ?? '').trim()).filter(Boolean))];
  if (!ids.length && !codes.length) throw new ComplianceError(422, 'legal_entity_invalid', 'name at least one building id or store code');
  if (ids.length + codes.length > MAX_ITEMS) throw new ComplianceError(422, 'legal_entity_invalid', `at most ${MAX_ITEMS} buildings per call`);
  const confirmed = opts.confirmed ?? true;
  const idSet = new Set(ids);
  const codeSet = new Set(codes);
  const rows = sites.filter((s) => idSet.has(s.id) || (s.isActive && s.code !== null && codeSet.has(s.code)));
  const byCode = new Map<string, SiteFacts[]>();
  for (const r of rows) if (r.code !== null && codeSet.has(r.code)) byCode.set(r.code, [...(byCode.get(r.code) ?? []), r]);
  const targets = new Map<string, SiteFacts>(rows.filter((r) => idSet.has(r.id)).map((r) => [r.id, r]));
  for (const matches of byCode.values()) if (matches.length === 1) targets.set(matches[0].id, matches[0]);
  const found = new Set(rows.filter((r) => idSet.has(r.id)).map((r) => r.id));
  const plan: AssignPlan = {
    assigned: [], moved: [], unchanged: [], kept_confirmed: [], wrong_country: [],
    unknown_buildings: ids.filter((i) => !found.has(i)),
    unmatched_codes: codes.filter((c) => !byCode.has(c)),
    ambiguous_codes: codes.filter((c) => (byCode.get(c)?.length ?? 0) > 1),
  };
  for (const [id, r] of [...targets.entries()].sort(([a], [b]) => a.localeCompare(b))) {
    const same = r.legalEntity === entity.id;
    if (r.country !== entity.country) plan.wrong_country.push(id);
    else if (same && (r.confirmed || !confirmed)) plan.unchanged.push(id);
    else if (r.confirmed && !confirmed) plan.kept_confirmed.push(id);
    else if (r.confirmed) {
      if (opts.moveConfirmed) plan.moved.push([id, r.legalEntity as string, entity.id]);
      else plan.kept_confirmed.push(id);
    } else plan.assigned.push(id);
  }
  return plan;
}

/** legal_entities.confirm: every site assigned to the company whose assignment is still assumed. */
export function planConfirm(entityId: string, sites: SiteFacts[]): string[] {
  return sites.filter((s) => s.legalEntity === entityId && !s.confirmed).map((s) => s.id).sort();
}

// ---- country responsible --------------------------------------------------------------------------------------------

export type ResponsibleChoice = { kind: 'contact'; id: string } | { kind: 'user'; id: string } | { kind: 'email'; email: string };

/** country_responsibles.set_responsible: exactly one of contact_id, user_id or email. */
export function responsibleChoice(body: { contact_id?: string | null; user_id?: string | null; email?: string | null }): ResponsibleChoice {
  const email = (body.email ?? '').trim() || null;
  const given = [body.contact_id ?? null, body.user_id ?? null, email].filter((v) => v !== null);
  if (given.length !== 1) throw new ComplianceError(422, 'responsible_invalid', 'Name exactly one of contact_id, user_id or email');
  if (body.contact_id) return { kind: 'contact', id: body.contact_id };
  if (body.user_id) return { kind: 'user', id: body.user_id };
  return { kind: 'email', email: email as string };
}

export interface CandidateContact { id: string; name: string; email: string }
export interface CandidateUser { id: string; name: string; email: string; linked: boolean }

/** country_responsibles.candidates: live contacts, and active users with no live contact yet (by link or email),
 *  each person once, ordered by name then id. */
export function candidates(contacts: CandidateContact[], users: CandidateUser[]) {
  const contactEmails = new Set(contacts.map((c) => c.email.toLowerCase()));
  // Sentinel orders by the stored name (an empty name first), then id, and only then shows the email for no name.
  const rows = [
    ...contacts.map((c) => ({ kind: 'contact' as const, id: c.id, raw: c.name ?? '', email: c.email })),
    ...users.filter((u) => !u.linked && !contactEmails.has(u.email.toLowerCase()))
      .map((u) => ({ kind: 'user' as const, id: u.id, raw: u.name ?? '', email: u.email.toLowerCase() })),
  ];
  return rows.sort((a, b) => a.raw.localeCompare(b.raw) || a.id.localeCompare(b.id))
    .map(({ raw, ...r }) => ({ ...r, name: raw || r.email }));
}

/** RESPONSE_SAMPLE_CAP: at most this many ids per list; the *_count fields carry the totals. */
export const capped = <T>(xs: T[]): T[] => xs.slice(0, RESPONSE_SAMPLE_CAP);
